import { LEXIQUE } from '../../lib/lexique';
import { libelle } from '../../lib/libelles';
import { formatDateHeure } from '../../lib/formats';

// Ce que la page Documents et la carte de document partagent: le nom lisible
// d'une pièce, l'état de sa lecture automatique et les champs qu'elle a lus.

export const typeDocument = (code) => libelle('documentType', code, 'Autre document');

export const formatDate = (iso) => formatDateHeure(iso);

// Extract optional custom label from file_name when doc_type is OTHER.
// Convention: file_name stored as "[Custom label] original.ext".
export const parseCustomLabel = (fileName) => {
  if (!fileName) return { label: null, base: fileName };
  const m = String(fileName).match(/^\[([^\]]+)\]\s*(.+)$/);
  return m ? { label: m[1], base: m[2] } : { label: null, base: fileName };
};

// L'extension telle qu'elle sera peinte sur la vignette. Le nom de fichier
// fait foi; le type MIME n'est qu'un repli, parce qu'un envoi mobile arrive
// parfois en `application/octet-stream`.
export const extensionDe = (doc) => {
  const { base } = parseCustomLabel(doc.file_name);
  const depuisNom = String(base || '').split('.').pop();
  if (depuisNom && depuisNom.length <= 4 && /^[a-z0-9]+$/i.test(depuisNom)) {
    return depuisNom.toUpperCase() === 'JPEG' ? 'JPG' : depuisNom.toUpperCase();
  }
  const mime = String(doc.file_type || '');
  if (mime.includes('pdf')) return 'PDF';
  if (mime.includes('png')) return 'PNG';
  if (mime.includes('webp')) return 'WEBP';
  if (mime.includes('jpeg') || mime.includes('jpg')) return 'JPG';
  return 'FIC';
};

export const DEAD_END_STATES = new Set(['failed', 'pdf_unreadable', 'file_missing']);

const OCR_BADGES = {
  reliable: 'bg-success/15 text-success',
  manual: 'bg-success/15 text-success',
  pending: 'bg-process/15 text-process',
  review: 'bg-flame/15 text-flame-ink',
  failed: 'bg-destructive/15 text-destructive',
  pdf_unreadable: 'bg-destructive/15 text-destructive',
  file_missing: 'bg-destructive/15 text-destructive',
};

export const getOcrState = (doc) => {
  if (doc.ocr_status?.key) {
    return {
      key: doc.ocr_status.key,
      label: doc.ocr_status.label || LEXIQUE.lectureAutomatique,
      badge: OCR_BADGES[doc.ocr_status.key] || 'bg-flame/15 text-flame-ink',
      text: String(doc.extracted_text || ''),
    };
  }
  // Repli pour une réponse antérieure au statut structuré: le score seul.
  const confidence = Number(doc.confidence_score || 0);
  const rawText = String(doc.extracted_text || '');
  if (confidence === 0) {
    return { key: 'failed', label: LEXIQUE.lectureImpossible, badge: OCR_BADGES.failed, text: rawText };
  }
  if (confidence >= 0.7) return { key: 'reliable', label: LEXIQUE.lectureFiable, badge: OCR_BADGES.reliable, text: rawText };
  return { key: 'review', label: LEXIQUE.lectureACompleter, badge: OCR_BADGES.review, text: rawText };
};

// Affichage seulement: un carburant lu en capitales (« GASOIL ») s'écrit en
// casse normale. Le texte extrait brut n'est jamais modifié.
const CARBURANTS_LUS = /\b(GASOIL|GAZOLE|DIESEL|ESSENCE|KÉROSÈNE|KEROSENE|PÉTROLE|PETROLE|SUPER)\b/g;
export const casseCarburant = (texte) =>
  String(texte ?? '').replace(CARBURANTS_LUS, (mot) => mot.charAt(0) + mot.slice(1).toLowerCase());

export const descriptionLisible = (texte) => casseCarburant(String(texte || '').replace(/\s+/g, ' ').trim());

// Ce que la lecture automatique sait trouver sur un document, nommé comme à l'écran.
const NOMS_CHAMPS_LUS = {
  bl_number: 'N° du bon de livraison',
  bl_date: 'Date du bon de livraison',
  delivered_volume: 'Volume livré (litres)',
  receipt_number: 'N° du reçu',
  receipt_date: 'Date du reçu',
  volume_liters: 'Volume (litres)',
  unit_price: 'Prix unitaire',
  amount: 'Montant',
  invoice_number: 'N° de facture',
  invoice_date: 'Date de la facture',
  declaration_number: 'N° de déclaration',
  declaration_date: 'Date de la déclaration',
  dates: 'Dates repérées',
  document_numbers: 'Numéros repérés',
  volumes_liters: 'Volumes repérés (litres)',
  amounts: 'Montants repérés',
  total: 'Total',
  date: 'Date',
  plate: 'Camion (plaque)',
  client_name: 'Client',
  amount_ht: 'Total hors taxes',
  tax_amount: 'Taxes (TVA)',
  rccm: 'RCCM',
  nif: 'NIF',
  siret: 'SIRET',
  id_last_name: 'Nom',
  id_first_name: 'Prénom(s)',
  id_number: 'N° de la pièce',
  id_birth_date: 'Date de naissance',
  id_expiry_date: "Date d'expiration",
};

// Nom lisible d'un champ lu sur le document: jamais la clé brute.
export const nomChamp = (cle) => {
  const base = String(cle || '').trim();
  return NOMS_CHAMPS_LUS[base] || NOMS_CHAMPS_LUS[base.toLowerCase()] || 'Information du document';
};

// Les montants lus sur une pièce ne s'affichent qu'à qui voit l'argent.
export const CHAMPS_MONTANT = new Set(['unit_price', 'amount', 'amounts', 'total', 'amount_ht', 'tax_amount']);

export const formatOcrValue = (value) => {
  if (Array.isArray(value)) return casseCarburant(value.slice(0, 3).join(', '));
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (value == null) return null;
  return casseCarburant(String(value));
};

// Les champs du type de pièce d'abord (n° de BL, volume livré…), les listes
// de valeurs repérées ensuite: limitée à six tuiles, la liste ne doit jamais
// laisser tomber le volume livré au profit des « dates repérées ».
export const ocrFieldEntries = (doc, { canSeeMoney = true } = {}) => {
  const prioritaires = (doc.ocr_manual_fields || []).map((field) => field.key);
  const rang = (cle) => {
    const index = prioritaires.indexOf(cle);
    if (index >= 0) return index;
    return Array.isArray((doc.ocr_fields || {})[cle]) ? 200 : 100;
  };
  return Object.entries(doc.ocr_fields || {})
    .filter(([cle]) => canSeeMoney || !CHAMPS_MONTANT.has(cle))
    .filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    })
    .sort(([a], [b]) => rang(a) - rang(b))
    .slice(0, 6);
};

// Statut d'un champ lu, tel que l'ont établi les contrôles du serveur
// (cohérence volume × prix, capacité de la citerne, camion de la flotte…).
export const controleChamp = (doc, cle) => (doc.ocr_field_checks || {})[cle] || null;
